// PocketBase клиент для admin SPA — тонкая обёртка над fetch (без зависимости
// от pocketbase-sdk). Совпадает по API с src/utils/editorialApi.ts десктоп-приложения,
// но добавляет CRUD-операции, доступные только редакторам.

const TOKEN_KEY = "reverium-editor-token";

// Default API base. Override через ?api=... в URL для staging.
function defaultApiBase(): string {
  // В dev (vite proxy) — относительный путь, чтобы proxy перехватил /api.
  if (
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname)
  ) {
    return "";
  }
  return "https://api.reverium.app";
}

// Public accessor — нужен для прямых fetch'ей за пределы стандартного
// PB CRUD (например, /api/editorial/og-fetch).
export function getApiBase(): string {
  return defaultApiBase();
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/** Событие «сессия протухла» — App.tsx слушает и возвращает на экран входа. */
export const SESSION_EXPIRED_EVENT = "reverium-admin-session-expired";

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const t = getToken();
  if (t) headers.set("Authorization", t);

  const res = await fetch(defaultApiBase() + path, { ...init, headers });

  // ── Протухшая сессия (баг Ивана 2026-07-24) ─────────────────────────────
  // Раньше 401 просто улетал ApiError'ом в вызывающий код: страницы молча не
  // грузили данные, и админка выглядела сломанной — «помогает только
  // перелогин». Теперь ловим централизованно: чистим токен и просим App
  // показать экран входа. Проверка `t` — реагируем только если токен БЫЛ
  // (иначе это обычный отказ логина с неверным паролем, не протухание).
  if (res.status === 401 && t && !path.includes("auth-with-password")) {
    setToken(null);
    try {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    } catch {
      /* ignore */
    }
  }

  if (!res.ok) {
    let msg = res.statusText;
    let data: unknown = null;
    try {
      data = await res.json();
      msg = (data as { message?: string }).message || msg;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg, data);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── Auth ───────────────────────────────────────────────────────────────────

interface AuthResponse {
  token: string;
  record: {
    id: string;
    email: string;
    name: string;
    avatar?: string;
    bio?: string;
    active: boolean;
  };
}

export async function loginEditor(
  email: string,
  password: string,
): Promise<AuthResponse> {
  const r = await api<AuthResponse>(
    "/api/collections/editors/auth-with-password",
    {
      method: "POST",
      body: JSON.stringify({ identity: email, password }),
    },
  );
  setToken(r.token);
  return r;
}

export async function authRefresh(): Promise<AuthResponse | null> {
  if (!getToken()) return null;
  try {
    const r = await api<AuthResponse>(
      "/api/collections/editors/auth-refresh",
      { method: "POST" },
    );
    setToken(r.token);
    return r;
  } catch {
    setToken(null);
    return null;
  }
}

export function logout(): void {
  setToken(null);
}

// ── Generic list ──────────────────────────────────────────────────────────

interface PbList<T> {
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  items: T[];
}

interface ListOpts {
  page?: number;
  perPage?: number;
  filter?: string;
  sort?: string;
  expand?: string;
}

export async function list<T>(
  collection: string,
  opts: ListOpts = {},
): Promise<PbList<T>> {
  const p = new URLSearchParams();
  if (opts.page) p.set("page", String(opts.page));
  if (opts.perPage) p.set("perPage", String(opts.perPage));
  if (opts.filter) p.set("filter", opts.filter);
  if (opts.sort) p.set("sort", opts.sort);
  if (opts.expand) p.set("expand", opts.expand);
  const q = p.toString() ? `?${p.toString()}` : "";
  return api<PbList<T>>(`/api/collections/${collection}/records${q}`);
}

export async function getOne<T>(
  collection: string,
  id: string,
  expand?: string,
): Promise<T> {
  const q = expand ? `?expand=${expand}` : "";
  return api<T>(`/api/collections/${collection}/records/${id}${q}`);
}

export async function createRecord<T>(
  collection: string,
  data: Record<string, unknown> | FormData,
): Promise<T> {
  return api<T>(`/api/collections/${collection}/records`, {
    method: "POST",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });
}

export async function updateRecord<T>(
  collection: string,
  id: string,
  data: Record<string, unknown> | FormData,
): Promise<T> {
  return api<T>(`/api/collections/${collection}/records/${id}`, {
    method: "PATCH",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });
}

export async function deleteRecord(
  collection: string,
  id: string,
): Promise<void> {
  await api<void>(`/api/collections/${collection}/records/${id}`, {
    method: "DELETE",
  });
}

export function fileUrl(
  collection: string,
  recordId: string,
  filename: string,
  thumb?: string,
): string {
  const base = `${defaultApiBase()}/api/files/${collection}/${recordId}/${encodeURIComponent(filename)}`;
  return thumb ? `${base}?thumb=${thumb}` : base;
}

export { ApiError };
